import React, { useState, useMemo } from 'react';
import { Modal } from 'semantic-ui-react';
import AzureForm from '../azure/components/form/azureForm';
import RequestForm3 from '../azure/components/form/RequestForm3';
import axios from 'axios';

const EditRequestModal = ({
    open,
    onClose,
    request,
    userEmail,
    userRoles,
    userCategories,
    userCategoriesSingleTenancy = [],
    userCategoriesMultiTenancy = [],
    onSuccess
}) => {
    const [successMessage, setSuccessMessage] = useState('');

    // Map request data to form initial values
    const initialValues = useMemo(() => {
        if (!request || !request.formData) return null;

        const formData = request.formData;

        // Check if this is a Multi Tenancy Form request
        const isForm3Request = formData.requestForm3 === true;

        // Handle Multi Tenancy Form requests differently
        if (isForm3Request) {
            // Map location for Multi Tenancy Form
            const locationName = formData.location?.location || '';
            let locationValue = '1'; // Default to Central India

            if (locationName.includes('CentralIndia') || locationName === 'CentralIndia') {
                locationValue = '1';
            } else if (locationName.includes('EastUS') || locationName === 'EastUS') {
                locationValue = '2';
            } else if (locationName.includes('WestUS3') || locationName === 'WestUS3') {
                locationValue = '3';
            } else if (locationName.includes('UAENorth') || locationName === 'UAENorth') {
                locationValue = '4';
            } else if (locationName.includes('GermanyWestCentral') || locationName === 'GermanyWestCentral' || locationName.toLowerCase().includes('germany')) {
                locationValue = '7';
            } else if (locationName.includes('CanadaCentral') || locationName === 'CanadaCentral' || locationName.toLowerCase().includes('canada')) {
                locationValue = '8';
            }

            // Map tenant based on category for Multi Tenancy Form
            // Multi Tenancy Form: *.parablu.com for DEV/QA, *.crashplan.com for STAGE/PROD
            let tenantValue = '1'; // Default to *.parablu.com
            if (['STAGE', 'PROD'].includes(formData.category)) {
                tenantValue = '4'; // *.crashplan.com
            }

            // Map storage destination for Multi Tenancy Form
            // Map from backend values to form values
            let storageDestValue = '';
            if (formData.storageDestination === 'Azure_Blob') {
                storageDestValue = '1'; // Azure Blob
            } else if (formData.storageDestination === 'S3_Compatibility') {
                storageDestValue = '2'; // CPG DataCenter
            } else if (formData.storageDestination === 'BYOS') {
                storageDestValue = '3'; // BYOS
            } else {
                // Legacy handling: if azureBlobHosted is true, assume Azure Blob
                storageDestValue = formData.azureBlobHosted ? '1' : '2';
            }

            return {
                tenant: tenantValue,
                location: locationValue,
                vmSize: '2',
                cloudName: formData.cloudName || '',
                storageDestination: storageDestValue,
                category: formData.category || '',
                diskSizeGB: formData.diskSizeGB?.toString() || '512',
                azureBlobHosted: formData.azureBlobHosted !== undefined ? formData.azureBlobHosted : (storageDestValue === '1'),
                azureBlobFuse: true,
                hasLicenseFile: !!formData.licenseFile,
                licenseFile: formData.licenseFile || null,
                expiryDays: formData.expiryDays || 7,
                tenantInfo: formData.tenantInfo || null,
                isMultiTenancySetup: true, // Always true for Multi Tenancy Form
                isMigrationSetup: formData.isMigrationSetup || false,
                oldTenantUrl: formData.oldTenantUrl || '',
                migrationCompletionDate: formData.migrationCompletionDate || '',
                isPOC: formData.isPOC || false
            };
        }

        // Regular form mapping (existing code)

        // Map tenant - find matching option by actual value
        const tenantOptions = [
            { value: '1', actual: '*.parablu.com' },
            { value: '2', actual: 'poc.crashplan.com' },
            { value: '3', actual: 'demo.crashplan.com' },
            { value: '4', actual: '*.crashplan.com' }
        ];
        const tenantOption = tenantOptions.find(opt => opt.actual === formData.tenant) || { value: '4' };

        // Map location - find matching option by location name
        // Location names are like "CentralIndia", "EastUS", "WestUS3", "UAENorth", "WestUS", "SouthEastAsia", "GermanyWestCentral", "CanadaCentral"
        const locationName = formData.location?.location || '';
        let locationValue = '';

        // Map location names to form values based on common patterns
        if (locationName.includes('CentralIndia') || locationName === 'CentralIndia') {
            locationValue = '1';
        } else if (locationName.includes('EastUS') || locationName === 'EastUS') {
            locationValue = '2';
        } else if (locationName.includes('WestUS3') || locationName === 'WestUS3') {
            locationValue = '3';
        } else if (locationName.includes('UAENorth') || locationName === 'UAENorth') {
            locationValue = '4';
        } else if ((locationName.includes('WestUS') || locationName === 'WestUS') && !locationName.includes('WestUS3')) {
            locationValue = '5';
        } else if (locationName.includes('SouthEastAsia') || locationName === 'SouthEastAsia') {
            locationValue = '6';
        } else if (locationName.includes('GermanyWestCentral') || locationName === 'GermanyWestCentral' || locationName.toLowerCase().includes('germany')) {
            locationValue = '7';
        } else if (locationName.includes('CanadaCentral') || locationName === 'CanadaCentral' || locationName.toLowerCase().includes('canada')) {
            locationValue = '8';
        }

        // Map storage destination
        const storageOptions = [
            { value: '1', actual: 'OD4B' },
            { value: '2', actual: 'Azure_Blob' },
            { value: '3', actual: 'Azure_Blob' },
            { value: '4', actual: 'Google_Drive' },
            { value: '5', actual: 'Others_Storage' }
        ];
        const storageOption = storageOptions.find(opt => opt.actual === formData.storageDestination);
        // If it's Azure_Blob, check azureBlobHosted to determine if it's '2' (CrashPlan) or '3' (Customer)
        let storageValue = storageOption?.value || '';
        if (formData.storageDestination === 'Azure_Blob') {
            storageValue = formData.azureBlobHosted ? '2' : '3';
        }

        // VM size is always '2' (Standard_D4as_v5) in the form
        const vmSizeValue = '2';

        return {
            tenant: tenantOption.value,
            location: locationValue,
            vmSize: vmSizeValue,
            cloudName: formData.cloudName || '',
            storageDestination: storageValue,
            category: formData.category || '',
            diskSizeGB: formData.diskSizeGB?.toString() || '512',
            azureBlobHosted: formData.azureBlobHosted || false,
            azureBlobFuse: formData.azureBlobFuse !== undefined ? formData.azureBlobFuse : true,
            hasLicenseFile: !!formData.licenseFile,
            licenseFile: formData.licenseFile || null,
            expiryDays: formData.expiryDays || 7,
            tenantInfo: formData.tenantInfo || null,
            isMultiTenancySetup: formData.isMultiTenancySetup !== undefined ? formData.isMultiTenancySetup : '',
            isMigrationSetup: formData.isMigrationSetup || false,
            migrationCompletionDate: formData.migrationCompletionDate || '',
            isPOC: formData.isPOC || false
        };
    }, [request]);

    const handleSubmit = async (formData) => {
        try {
            const response = await axios.post('https://qa-tenantbridge.parablu.com/api/update-request', {
                requestId: request.id,
                formData: {
                    ...formData,
                    submittedBy: userEmail
                }
            }, { withCredentials: true });

            if (response.data.success) {
                setSuccessMessage(response.data.message || 'Request updated successfully');
                if (onSuccess) {
                    onSuccess();
                }
                // Close modal after a brief delay to show success message
                setTimeout(() => {
                    onClose();
                    setSuccessMessage('');
                }, 1500);
            }

            return {
                success: true,
                message: response.data.message
            };
        } catch (error) {
            console.error('Update error:', error);
            return {
                success: false,
                error: error.response?.data?.message || 'An unexpected error occurred'
            };
        }
    };

    if (!request || !request.formData) return null;

    // Check if this is a Multi Tenancy Form request
    const isForm3Request = request?.formData?.requestForm3 === true;

    const effectiveCategories = isForm3Request
        ? (userCategoriesMultiTenancy.length > 0 ? userCategoriesMultiTenancy : userCategories)
        : (userCategoriesSingleTenancy.length > 0 ? userCategoriesSingleTenancy : userCategories);

    return (
        <Modal
            open={open}
            onClose={onClose}
            size="large"
            closeIcon
        >
            <Modal.Header>Edit Request - {request.id}</Modal.Header>
            <Modal.Content scrolling>
                {successMessage && (
                    <div style={{ marginBottom: '1rem', color: '#6F9911', fontWeight: 'bold' }}>
                        {successMessage}
                    </div>
                )}
                {isForm3Request ? (
                    <RequestForm3
                        onSubmit={handleSubmit}
                        userEmail={userEmail}
                        roles={userRoles}
                        categories={effectiveCategories}
                        initialValues={initialValues}
                        originalCloudName={request?.formData?.cloudName || null}
                    />
                ) : (
                    <AzureForm
                        onSubmit={handleSubmit}
                        userEmail={userEmail}
                        roles={userRoles}
                        categories={effectiveCategories}
                        initialValues={initialValues}
                        originalCloudName={request?.formData?.cloudName || null}
                    />
                )}
            </Modal.Content>
        </Modal>
    );
};

export default EditRequestModal;

